import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HTMLFlipBook from 'react-pageflip';
import { ChevronLeft, ChevronRight, X, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookReader = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const bookRef = useRef();
  const loadedPagesRef = useRef(new Set());
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageImages, setPageImages] = useState({});

  // Fetch a single page image as a blob URL from the server
  const fetchPageImage = useCallback(async (pageNumber) => {
    if (loadedPagesRef.current.has(pageNumber)) return null;
    loadedPagesRef.current.add(pageNumber);

    try {
      const response = await axios.get(
        `${API_URL}/books/${bookId}/page/${pageNumber}`,
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      return URL.createObjectURL(response.data);
    } catch (error) {
      loadedPagesRef.current.delete(pageNumber);
      return null;
    }
  }, [bookId, getAuthHeader]);

  // Load a batch of pages in parallel
  const loadPages = useCallback(async (pageNumbers) => {
    const toLoad = pageNumbers.filter(p => !loadedPagesRef.current.has(p) && p >= 1 && p <= totalPages);
    if (toLoad.length === 0) return;

    const results = await Promise.all(
      toLoad.map(async (num) => {
        const url = await fetchPageImage(num);
        return url ? [num, url] : null;
      })
    );

    const newImages = {};
    for (const result of results) {
      if (result) newImages[result[0]] = result[1];
    }

    if (Object.keys(newImages).length > 0) {
      setPageImages(prev => ({ ...prev, ...newImages }));
    }
  }, [fetchPageImage, totalPages]);

  // Load book metadata and verify ownership
  const loadBook = useCallback(async () => {
    try {
      const [bookRes, myBooksRes] = await Promise.all([
        axios.get(`${API_URL}/books/${bookId}`),
        axios.get(`${API_URL}/my-books`, { headers: getAuthHeader() })
      ]);

      const bookData = bookRes.data;
      const ownsBook = myBooksRes.data.some(b => b.id === bookId);

      if (!ownsBook) {
        toast.error('You do not own this book');
        navigate('/my-books');
        return;
      }

      setBook(bookData);
      setTotalPages(bookData.pages);
    } catch (error) {
      toast.error('Failed to load book');
      navigate('/my-books');
    } finally {
      setLoading(false);
    }
  }, [bookId, getAuthHeader, navigate]);

  useEffect(() => {
    if (user) loadBook();
  }, [user, loadBook]);

  // Once book is loaded, fetch the first few pages
  useEffect(() => {
    if (!totalPages) return;
    // Load first 4 pages immediately
    const initial = [];
    for (let i = 1; i <= Math.min(4, totalPages); i++) initial.push(i);
    loadPages(initial);
  }, [totalPages, loadPages]);

  const onFlip = useCallback((e) => {
    setCurrentPage(e.data);
    // Pre-fetch pages around the current position
    const center = e.data + 1;
    const ahead = [];
    for (let i = center; i <= Math.min(center + 6, totalPages); i++) ahead.push(i);
    loadPages(ahead);
  }, [totalPages, loadPages]);

  const nextPage = () => bookRef.current?.pageFlip()?.flipNext();
  const prevPage = () => bookRef.current?.pageFlip()?.flipPrev();

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
      setIsFullscreen(!isFullscreen);
    } catch {
      // Fullscreen blocked by permissions policy
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(pageImages).forEach(url => {
        if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      });
    };
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Login Required</h2>
          <Button onClick={() => navigate('/admin/login')}>Login</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading book...</p>
        </div>
      </div>
    );
  }

  if (!book) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-md border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/my-books')}
              className="text-white hover:text-gray-300 h-8 w-8 sm:h-10 sm:w-10"
              data-testid="close-reader-btn"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </Button>
            <div className="max-w-[200px] sm:max-w-none">
              <h1 className="text-white font-bold text-sm sm:text-lg truncate">{book.title}</h1>
              <p className="text-gray-400 text-xs sm:text-sm truncate hidden sm:block">{book.author}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <span className="text-gray-400 text-xs sm:text-sm">
              {currentPage + 1} / {totalPages + 2}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleFullscreen}
              className="text-white hover:text-gray-300 h-8 w-8 sm:h-10 sm:w-10 hidden sm:flex"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4 sm:h-5 sm:w-5" /> : <Maximize2 className="h-4 w-4 sm:h-5 sm:w-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Book Container */}
      <div className="flex items-start justify-center px-2 sm:px-4" style={{ minHeight: '100vh', paddingTop: '80px', paddingBottom: '100px' }}>
        <div className="relative w-full flex items-start justify-center" style={{ maxWidth: '100%' }}>
          <HTMLFlipBook
            width={typeof window !== 'undefined' && window.innerWidth < 640 ? window.innerWidth - 32 : 340}
            height={typeof window !== 'undefined' && window.innerWidth < 640 ? window.innerWidth * 1.33 - 32 : 450}
            size="stretch"
            minWidth={210}
            maxWidth={600}
            minHeight={280}
            maxHeight={750}
            maxShadowOpacity={0.5}
            showCover={true}
            mobileScrollSupport={true}
            onFlip={onFlip}
            className="shadow-2xl mx-auto"
            ref={bookRef}
            usePortrait={typeof window !== 'undefined' && window.innerWidth < 640}
            drawShadow={true}
          >
            {/* Cover Page */}
            <div className="page bg-white flex items-center justify-center p-8" data-testid="book-cover">
              <div className="text-center w-full h-full flex items-center justify-center">
                <img
                  src={book.coverImage}
                  alt={book.title}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-xl"
                />
              </div>
            </div>

            {/* Page Images from Server */}
            {Array.from({ length: book.pages }, (_, i) => {
              const pageNumber = i + 1;
              const pageUrl = pageImages[pageNumber];

              return (
                <div key={`page-${pageNumber}`} className="page bg-white flex items-center justify-center p-2">
                  <div className="w-full h-full flex items-center justify-center">
                    {pageUrl ? (
                      <img
                        src={pageUrl}
                        alt={`Page ${pageNumber}`}
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                          <p className="text-gray-400 text-sm">Page {pageNumber}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Back Cover */}
            <div className="page bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-8">
              <div className="text-center text-white">
                <h3 className="text-2xl font-bold mb-4">The End</h3>
                <p className="mb-6">Thank you for reading!</p>
                <div className="text-sm opacity-75">
                  Learning Smart
                </div>
              </div>
            </div>
          </HTMLFlipBook>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="fixed bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-black/50 backdrop-blur-md rounded-full px-2 py-2 flex items-center space-x-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={prevPage}
            disabled={currentPage === 0}
            className="text-white hover:text-gray-300 disabled:opacity-30 h-10 w-10 sm:h-12 sm:w-12"
            data-testid="prev-page-btn"
          >
            <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={nextPage}
            disabled={currentPage >= totalPages + 1}
            className="text-white hover:text-gray-300 disabled:opacity-30 h-10 w-10 sm:h-12 sm:w-12"
            data-testid="next-page-btn"
          >
            <ChevronRight className="h-6 w-6 sm:h-7 sm:w-7" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookReader;
